import React, { useEffect, useState } from "react";
import { Table, Tag, Image, Modal } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { FaImages } from "react-icons/fa";
import { deleteProperty, getAllProperty } from "../api/propertyApi";
import { EditTwoTone, DeleteTwoTone } from "@ant-design/icons";
import styled from "styled-components";

const TableContainer = styled.div`
  width: 80%;
  margin: auto;
  background-color: white;
  height: 100%;
  padding: 20px;
`;

const PropertyDataList = () => {
  const [propertyList, setPropertyList] = useState([]);
  const [visibleImages, setVisibleImages] = useState({});
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  const navigate = useNavigate();

  const handleDelete = (id) => {
    setDeleteModalVisible(true);
    setRecordToDelete(id);
  };

  const handleEdit = (id) => {
    navigate(`/propertyDetailsEdit/${id}`);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteProperty(recordToDelete);
      // Handle success or show a success message
      Modal.success({
        title: "Delete Successful",
        content: "The property has been deleted successfully.",
      });
      // Refresh data or perform any other actions after successful deletion
      fetchProperty();
    } catch (error) {
      // Handle error or show an error message
      Modal.error({
        title: "Delete Failed",
        content: "An error occurred while deleting the property.",
      });
    } finally {
      setDeleteModalVisible(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalVisible(false);
  };

  const handleImageClick = (rowKey) => {
    setVisibleImages((prevState) => ({
      ...prevState,
      [rowKey]: !prevState[rowKey],
    }));
  };

  const fetchProperty = async () => {
    try {
      const data = await getAllProperty();
      if (data) {
        setPropertyList(data);
        setVisibleImages({});
      }
    } catch (error) {
      console.error("Error fetching property data:", error);
    }
  };

  useEffect(() => {
    fetchProperty();
  }, []);

  const columns = [
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
    },
    {
      title: "Property Size",
      dataIndex: "propertySize",
      key: "propertySize",
    },
    {
      title: "Property Type",
      dataIndex: "propertyType",
      key: "propertyType",
    },
    {
      title: "Images",
      dataIndex: "images",
      key: "images",
      render: (images, record) => (
        <>
          <Image
            width={200}
            preview={{
              visible: false,
            }}
            src={images[0]?.url}
            onClick={() => handleImageClick(record._id)}
          />
          <div style={{ display: "none" }}>
            <Image.PreviewGroup
              preview={{
                visible: visibleImages[record._id],
                onVisibleChange: (vis) => handleImageClick(record._id),
              }}
            >
              {images.map((image, index) => (
                <Image key={index} src={image.url} />
              ))}
            </Image.PreviewGroup>
          </div>
        </>
      ),
    },
    {
      title: "Tags",
      dataIndex: "isFeatured",
      key: "isFeatured",
      render: (isFeatured, record) => (
        <>
          {isFeatured && <Tag color="geekblue">Featured</Tag>}
          <Tag color={record.status === "jual" ? "green" : "orange"}>
            {record.status}
          </Tag>
        </>
      ),
    },
    {
      title: "Action",
      dataIndex: "_id",
      key: "_id",
      render: (_, record) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-evenly",
          }}
        >
          <EditTwoTone onClick={() => handleEdit(record._id)} />
          <DeleteTwoTone onClick={() => handleDelete(record._id)} />
        </div>
      ),
    },
  ];

  return (
    <TableContainer>
      <Table dataSource={propertyList} columns={columns} />

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Delete"
        visible={deleteModalVisible}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
        okText="Yes"
        cancelText="No"
      >
        <p>Are you sure you want to delete this property?</p>
      </Modal>
    </TableContainer>
  );
};

export default PropertyDataList;
